import React, { PureComponent } from 'react';
import * as R from 'ramda';
import classNames from 'classnames';

import Button from './Button';
import Tooltip from './Tooltip';

export const HOVER_BUTTON_PROPS = ['componentShownOnHover', 'textShownOnHover', 'onHover', 'activeChild', 'position'];

type Props = {
  /**
   * Everything rendered inside the Hover Button.
   */
  children: React.ReactNode;

  /**
   * Optional class - if passed, it's set on a root level of the component.
   */
  containerClassName?: string;
  /**
   * If passed in, will be placed within core Tooltip component to show
   * on hover against a white background
   */
  componentShownOnHover?: React.ReactNode;
  /**
   * If passed in, will be displayed below the button on hover against a dark
   * grey background
   */
  textShownOnHover?: string;
  /**
   * Optional function to fire on hover (mixpanel event will
   * automatically be triggered regardless).
   */
  onHover?: () => void;
  /**
   * Optional element to show on hover only. If not passed in,
   * OR not hovering, the child element will be shown instead.
   */
  activeChild?: React.ReactNode;
  /**
   * Where to position the tooltip if componentShownOnHover is used
   */
  position?: string;
  /**
   * Any other props are passed directly down to the Button component
   */
} & Record<string, any>;

type State = {
  isHovering: boolean;
};

class HoverButton extends PureComponent<Props, State> {
  constructor(props) {
    super(props);
    this.state = {
      isHovering: false,
    };
  }

  _onHoverEnter = () => {
    this.props.onHover && this.props.onHover();
  };

  _onHoverLeave = () => {
    this.setState({ isHovering: false });
  };

  render() {
    const defaultProps = {
      testid: 'hover-button',
    };
    const props = {
      ...defaultProps,
      ...this.props,
    };
    const propsToOmitFromButton = [...HOVER_BUTTON_PROPS, 'containerClassName'];
    const buttonProps = R.omit(propsToOmitFromButton, props);
    let buttonChild = props.children;
    if (props.activeChild && this.state.isHovering) {
      buttonChild = props.activeChild;
    }

    const button = <Button {...buttonProps}>{buttonChild}</Button>;

    if (props.componentShownOnHover) {
      return (
        <div
          onMouseOver={this._onHoverEnter}
          onMouseOut={this._onHoverLeave}
          className={classNames(
            {
              'h_d-ib': !props.containerClassName,
            },
            props.containerClassName
          )}
        >
          <Tooltip html={props.componentShownOnHover as React.ReactElement} position={props.position}>
            <div>{button}</div>
          </Tooltip>
        </div>
      );
    }

    return (
      <div onMouseOver={this._onHoverEnter} onMouseOut={this._onHoverLeave}>
        {button}
        <div
          data-testid="hover-button-container"
          className={classNames('hover-button__outer-container', {
            'h_d-n': !this.state.isHovering,
          })}
        >
          <div className="hover-button__inner-container">{props.textShownOnHover}</div>
        </div>
      </div>
    );
  }
}

export default HoverButton;
